import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, AlertIcon, Badge, Box, Button, Flex, Heading, SimpleGrid, Text } from "@chakra-ui/react";
import { getDeadLetters, getMetrics, getRequests, getReview } from "../../utils/apis/agentApi";
import { RegistrationOverview } from "../../pages/registrations";

type Snapshot = { metrics: any; review: any[] | null; failures: any[] | null; requests: any[] | null; errors: string[]; at: Date };
const number = (value: unknown) => typeof value === "number" && Number.isFinite(value) ? value.toLocaleString() : "Unavailable";
const capped = (rows: any[] | null) => rows ? `${rows.length}${rows.length >= 200 ? "+" : ""}` : "Unavailable";

export default function DashboardContent() {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const busy = useRef(false);
  const generation = useRef(0);
  const load = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    const current = generation.current;
    setLoading(true);
    const results = await Promise.allSettled([getMetrics(), getReview(200), getDeadLetters(200), getRequests("newest", undefined, 20)]);
    if (current !== generation.current) return;
    const errors: string[] = [];
    const labels = ["Agent metrics", "Review queue", "Failed messages", "Recent requests"];
    const data = results.map((result, i) => {
      if (result.status === "fulfilled" && result.value.ok && result.value.data != null) return result.value.data;
      errors.push(labels[i]); return null;
    });
    const array = (value: any, label: string) => {
      if (Array.isArray(value)) return value;
      if (!errors.includes(label)) errors.push(label);
      return null;
    };
    setSnapshot({metrics: data[0], review: array(data[1], labels[1]), failures: array(data[2]?.dead_letters, labels[2]),
      requests: array(data[3]?.requests, labels[3]), errors, at: new Date()});
    busy.current = false;
    setLoading(false);
  }, []);
  useEffect(() => {
    void load();
    const refresh = () => { if (!document.hidden) void load(); };
    const timer = window.setInterval(refresh, 60000);
    document.addEventListener("visibilitychange", refresh);
    // Invalidate asynchronous requests on unmount, including StrictMode remounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { generation.current++; busy.current = false; window.clearInterval(timer); document.removeEventListener("visibilitychange", refresh); };
  }, [load]);
  const week = snapshot?.metrics?.this_week;
  const tiles = [
    {label: "Open review issues", value: snapshot ? capped(snapshot.review) : "Loading", to: "/flag-review", note: "Issues awaiting a human review"},
    {label: "Failed messages", value: snapshot ? capped(snapshot.failures) : "Loading", to: "/dead-letters", note: "Messages that could not be processed"},
    {label: "Provider introductions", value: snapshot ? number(week?.intros_created) : "Loading", to: "/requests", note: "Last 7 days - introductions, not unique jobs"},
    {label: "No provider found", value: snapshot ? number(week?.no_worker_found_total) : "Loading", to: "/waitlist", note: "Last 7 days - recorded no-match events"},
  ];
  return <Box p={{base: 3, md: 5}}>
    <Flex justify="space-between" align="start" gap={3} wrap="wrap" mb={5}>
      <Box><Heading size="lg">Dashboard</Heading><Text color="admin.muted" mt={1}>Registrations, requests, conversations and work needing attention.</Text>
        <Text fontSize="sm" color="admin.muted" role="status" mt={2}>
          {snapshot ? `Last checked ${snapshot.at.toLocaleTimeString()}. Refreshes every minute while this page is visible.` : "Loading current activity..."}
        </Text></Box>
      <Button onClick={load} isLoading={loading} colorScheme="purple">Refresh</Button>
    </Flex>
    <RegistrationOverview refresh={snapshot?.at.getTime() || 0} />
    {!!snapshot?.errors.length && <Alert status="warning" mb={4} borderRadius="md"><AlertIcon />
      <Text>Could not refresh: {snapshot.errors.join(", ")}. These sections are marked unavailable. Try Refresh.</Text>
    </Alert>}
    <SimpleGrid columns={{base: 1, sm: 2, xl: 4}} spacing={4}>
      {tiles.map(tile => <Box as={Link} to={tile.to} key={tile.label} borderWidth="1px" borderRadius="xl" p={5} bg="admin.surface" _hover={{bg: "admin.subtle"}}>
        <Text fontWeight="semibold">{tile.label}</Text><Text fontSize="3xl" fontWeight="bold" color="admin.accent" my={2}>{tile.value}</Text>
        <Text fontSize="sm" color="admin.muted">{tile.note}</Text>
      </Box>)}
    </SimpleGrid>
    <Text color="admin.muted" fontSize="xs" mt={2}>Queue counts show up to 200 items; 200+ means more may be waiting.</Text>
    <Flex gap={3} wrap="wrap" my={5}>
      <Button as={Link} to="/conversations" colorScheme="purple">Open conversations</Button>
      <Button as={Link} to="/requests">Manage requests</Button>
      <Button as={Link} to="/verification">Review providers</Button>
      <Button as={Link} to="/agent-metrics" variant="outline">All agent metrics</Button>
    </Flex>
    <Box borderWidth="1px" borderRadius="xl" p={{base: 3, md: 5}} bg="admin.surface">
      <Heading size="md" mb={1}>Recent requests</Heading><Text color="admin.muted" fontSize="sm" mb={4}>The latest 20 request groups. Repeat contact records are grouped on the Requests page.</Text>
      {!snapshot ? <Text>Loading requests...</Text> : snapshot.requests === null ? <Text>Recent requests unavailable.</Text> : !snapshot.requests.length ? <Text>No requests yet.</Text> :
        <Box as="ul" listStyleType="none" m={0} p={0}>{snapshot.requests.map((request: any) => <Box as="li" key={`${request.kind}:${request.id}`} py={3} borderTopWidth="1px">
          <Flex justify="space-between" gap={3} wrap="wrap"><Box minW={0}>
            <Text fontWeight="semibold" overflowWrap="anywhere">{request.service || "Service not specified"}</Text>
            <Text fontSize="sm" color="admin.muted" overflowWrap="anywhere">{request.location || "Location not recorded"}</Text>
          </Box><Badge alignSelf="start" colorScheme="purple">{String(request.status || "unknown").replace(/_/g, " ")}</Badge></Flex>
          <Text fontSize="xs" color="admin.muted" mt={1}>{request.reference} - {request.created_at ? new Date(request.created_at).toLocaleString() : "Date not recorded"}</Text>
        </Box>)}</Box>}
      <Button as={Link} to="/requests" variant="link" mt={4} minH="44px">View all requests</Button>
    </Box>
  </Box>;
}
